// AnalyticsDashboard.js - Enhanced order analytics with Recharts
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// Chart components
import { LineChart, PieChart, BarChart } from './analytics/charts';

// Filter and widget components
import FilterBar from './analytics/FilterBar';
import DashboardGrid from './analytics/DashboardGrid';
import { Widget, ComparisonWidget } from './analytics/widgets';

// Utils
import { exportAnalyticsData } from './analytics/utils/exportUtils';

// Format date as YYYY-MM-DD in local timezone (avoids UTC conversion issues)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getBaseURL = () => {
  const hostname = window.location.hostname;
  if (hostname === 'dev.barriobucks.com') {
    return 'https://dev.barriobucks.com';
  }
  return 'https://barriobucks.com';
};

const api = axios.create({
  baseURL: `${getBaseURL()}/api`,
  withCredentials: true,
});

function AnalyticsDashboard() {
  // Tab state - persisted to localStorage
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('analyticsDashboardTab') || 'analytics';
  });

  // Filter state
  const [period, setPeriod] = useState('week');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sources, setSources] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [availableTags, setAvailableTags] = useState([]);

  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [bySource, setBySource] = useState([]);
  const [byDate, setByDate] = useState([]);
  const [byHour, setByHour] = useState([]);
  const [tags, setTags] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [tipsBySource, setTipsBySource] = useState([]);
  const [tipsByType, setTipsByType] = useState([]);
  const [payments, setPayments] = useState([]);
  const [cashRecon, setCashRecon] = useState([]);
  const [itemRevenue, setItemRevenue] = useState([]);
  const [itemTrend, setItemTrend] = useState([]);
  const [selectedTrendItems, setSelectedTrendItems] = useState([]);
  const [topCustomersByTag, setTopCustomersByTag] = useState([]);
  const [tipsByDiningType, setTipsByDiningType] = useState([]);

  // Order history state
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderPagination, setOrderPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [orderSort, setOrderSort] = useState({ sortBy: 'order_date', sortDir: 'DESC' });
  const [orderFilters, setOrderFilters] = useState({ source: '', platform: '', paymentType: '', orderType: '', search: '', promoCode: '' });
  const [orderLoading, setOrderLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [availablePromoCodes, setAvailablePromoCodes] = useState([]);

  // Sound state for new customer chime
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('newCustomerSoundEnabled') === 'true';
  });
  const audioContextRef = useRef(null);
  const lastMaxOrderIdRef = useRef(null);
  const isPollingRef = useRef(false);

  // Persist tab selection
  useEffect(() => {
    localStorage.setItem('analyticsDashboardTab', activeTab);
  }, [activeTab]);

  // Persist sound preference
  useEffect(() => {
    localStorage.setItem('newCustomerSoundEnabled', soundEnabled);
  }, [soundEnabled]);

  // Build source filter param
  const getSourceParam = () => {
    if (selectedSources.length === 0 || selectedSources.length === sources.length) {
      return undefined;
    }
    return selectedSources.join(',');
  };

  // Build order type filter param
  const getOrderTypeParam = () => {
    if (selectedOrderTypes.length === 0 || selectedOrderTypes.length === orderTypes.length) {
      return undefined;
    }
    return selectedOrderTypes.join(',');
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        period: period !== 'custom' ? period : undefined,
        startDate: period === 'custom' ? startDate : undefined,
        endDate: period === 'custom' ? endDate : undefined,
        source: getSourceParam(),
        orderType: getOrderTypeParam(),
        ...(selectedTag && { tag: selectedTag }),
        ...(itemSearch && { item: itemSearch })
      };

      const fetchPromises = [
        api.get('/stats/overview', { params }),
        api.get('/stats/sources'),
        api.get('/stats/order-types').catch(() => ({ data: [] })),
        api.get('/stats/by-source', { params }),
        api.get('/stats/by-date', { params }),
        api.get('/stats/by-hour', { params }),
        api.get('/stats/tags', { params: { ...params, limit: 10 } }),
        api.get('/stats/top-items', { params: { ...params, limit: 10 } }),
        api.get('/stats/tips', { params }).catch(() => ({ data: [] })),
        api.get('/stats/tips-by-type', { params }).catch(() => ({ data: [] })),
        api.get('/stats/payments', { params }).catch(() => ({ data: [] })),
        api.get('/stats/cash-reconciliation', { params }).catch(() => ({ data: [] })),
        api.get('/stats/tags/list').catch(() => ({ data: [] })),
        api.get('/stats/items/revenue', { params: { ...params, limit: 25 } }).catch(() => ({ data: [] })),
        api.get('/stats/tips-by-dining-type', { params }).catch(() => ({ data: [] })),
        selectedTag
          ? api.get('/stats/top-customers-by-tag', { params: { ...params, tag: selectedTag, limit: 20 } }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        api.get('/stats/promo-codes/list').catch(() => ({ data: [] }))
      ];

      const [overviewRes, sourcesListRes, orderTypesRes, bySourceRes, byDateRes, byHourRes, tagsRes, itemsRes, tipsRes, tipsByTypeRes, paymentsRes, cashReconRes, tagsListRes, itemRevenueRes, tipsByDiningTypeRes, topCustomersByTagRes, promoCodesListRes] = await Promise.all(fetchPromises);

      setOverview(overviewRes.data);
      setSources(sourcesListRes.data || []);
      setOrderTypes(orderTypesRes.data || []);
      setBySource(bySourceRes.data || []);
      setByDate(byDateRes.data || []);
      setByHour(byHourRes.data || []);
      setTags(tagsRes.data || []);
      setTopItems(itemsRes.data || []);
      setTipsBySource(tipsRes.data || []);
      setTipsByType(tipsByTypeRes.data || []);
      setPayments(paymentsRes.data || []);
      setCashRecon(cashReconRes.data || []);
      setAvailableTags(tagsListRes.data || []);
      setItemRevenue(itemRevenueRes.data || []);
      setTipsByDiningType(tipsByDiningTypeRes.data || []);
      setTopCustomersByTag(topCustomersByTagRes.data || []);
      setAvailablePromoCodes(promoCodesListRes.data || []);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err.response?.data?.error || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate, selectedSources, selectedOrderTypes, selectedTag, itemSearch, sources.length, orderTypes.length]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch item trend data when selected items change
  const fetchItemTrend = useCallback(async (items) => {
    if (!items || items.length === 0) {
      setItemTrend([]);
      return;
    }
    try {
      const params = {
        period: period !== 'custom' ? period : undefined,
        startDate: period === 'custom' ? startDate : undefined,
        endDate: period === 'custom' ? endDate : undefined,
        items: items.join(',')
      };
      const res = await api.get('/stats/items/trend', { params });
      setItemTrend(res.data || []);
    } catch (err) {
      console.error('Error fetching item trend:', err);
    }
  }, [period, startDate, endDate]);

  const handleTrendItemToggle = (itemName) => {
    setSelectedTrendItems(prev => {
      const next = prev.includes(itemName)
        ? prev.filter(i => i !== itemName)
        : [...prev, itemName].slice(0, 5); // max 5 items
      fetchItemTrend(next);
      return next;
    });
  };

  const fetchOrderHistory = useCallback(async (page = 1) => {
    setOrderLoading(true);
    try {
      const params = {
        period: period !== 'custom' ? period : undefined,
        startDate: period === 'custom' ? startDate : undefined,
        endDate: period === 'custom' ? endDate : undefined,
        page,
        limit: orderPagination.limit,
        sortBy: orderSort.sortBy,
        sortDir: orderSort.sortDir,
        ...(orderFilters.source && { source: orderFilters.source }),
        ...(orderFilters.platform && { platform: orderFilters.platform }),
        ...(orderFilters.paymentType && { paymentType: orderFilters.paymentType }),
        ...(orderFilters.orderType && { orderType: orderFilters.orderType }),
        ...(orderFilters.search && { search: orderFilters.search }),
        ...(orderFilters.promoCode && { promoCode: orderFilters.promoCode }),
      };
      const res = await api.get('/stats/orders', { params });
      const orders = res.data.orders || [];
      setOrderHistory(orders);
      setOrderPagination(res.data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });

      // Track max order ID for polling
      if (orders.length > 0) {
        const maxId = Math.max(...orders.map(o => o.id));
        lastMaxOrderIdRef.current = maxId;
      }
    } catch (err) {
      console.error('Error fetching order history:', err);
    } finally {
      setOrderLoading(false);
    }
  }, [period, startDate, endDate, orderPagination.limit, orderSort, orderFilters]);

  // Fetch orders when on Orders tab or when filters change
  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrderHistory(1);
    }
  }, [activeTab, period, startDate, endDate, selectedSources, selectedOrderTypes, orderFilters, orderSort]);

  // Auto-refresh orders every 30 seconds when on Orders tab
  useEffect(() => {
    if (activeTab !== 'orders') return;

    const pollNewOrders = async () => {
      if (!lastMaxOrderIdRef.current) return;
      if (isPollingRef.current) return; // Prevent concurrent polls
      isPollingRef.current = true;

      try {
        const params = {
          period: period !== 'custom' ? period : undefined,
          startDate: period === 'custom' ? startDate : undefined,
          endDate: period === 'custom' ? endDate : undefined,
          sinceId: lastMaxOrderIdRef.current,
          limit: 50,
          sortBy: 'id',
          sortDir: 'DESC',
        };
        const res = await api.get('/stats/orders', { params });
        const newOrders = res.data.orders || [];

        if (newOrders.length > 0) {
          // Prepend new orders without resetting filters
          setOrderHistory(prev => [...newOrders, ...prev]);
          setOrderPagination(prev => ({
            ...prev,
            total: prev.total + newOrders.length
          }));

          // Update max order ID
          const maxId = Math.max(...newOrders.map(o => o.id));
          lastMaxOrderIdRef.current = maxId;

          // Check for new customers and play chime
          const hasNewCustomer = newOrders.some(o => o.is_new_customer);
          if (hasNewCustomer && soundEnabled) {
            playChime();
          }
        }
      } catch (err) {
        console.error('Error polling for new orders:', err);
      } finally {
        isPollingRef.current = false;
      }
    };

    const intervalId = setInterval(pollNewOrders, 30000);
    return () => clearInterval(intervalId);
  }, [activeTab, period, startDate, endDate, soundEnabled]);

  // Play chime sound for new customers
  const playChime = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;

      // Create a pleasant chime sound
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5
      oscillator.frequency.setValueAtTime(1108.73, ctx.currentTime + 0.1); // C#6
      oscillator.frequency.setValueAtTime(1318.51, ctx.currentTime + 0.2); // E6

      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
    } catch (err) {
      console.error('Error playing chime:', err);
    }
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleExport = () => {
    exportAnalyticsData({
      overview,
      bySource,
      byDate,
      topItems,
      period,
      startDate,
      endDate
    });
  };

  // Format helpers
  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateWithDay = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00'); // noon to avoid timezone issues
    const dayAbbrevs = ['Su', 'M', 'T', 'W', 'R', 'F', 'Sa'];
    const dayAbbr = dayAbbrevs[d.getDay()];
    return `${dayAbbr} ${d.getMonth() + 1}/${d.getDate()}`;
  };

  const formatHour = (hour) => {
    const h = parseInt(hour);
    if (h === 0) return '12am';
    if (h < 12) return `${h}am`;
    if (h === 12) return '12pm';
    return `${h - 12}pm`;
  };

  // Prepare chart data - fill missing days with 0
  const prepareLineChartData = () => {
    if (byDate.length === 0) return [];

    // Build a lookup from existing data
    const dataByDate = {};
    byDate.forEach(d => {
      const dateKey = typeof d.order_date === 'string' ? d.order_date.slice(0, 10) : d.order_date;
      dataByDate[dateKey] = {
        orders: parseInt(d.order_count) || 0,
        revenue: parseFloat(d.revenue) || 0
      };
    });

    // Determine date range from overview or data
    const dates = Object.keys(dataByDate).sort();
    const rangeStart = overview?.startDate || dates[0];
    const rangeEnd = overview?.endDate || dates[dates.length - 1];

    // Generate all dates in range
    const result = [];
    const current = new Date(rangeStart + 'T12:00:00');
    const end = new Date(rangeEnd + 'T12:00:00');

    while (current <= end) {
      const key = current.getFullYear() + '-' +
        String(current.getMonth() + 1).padStart(2, '0') + '-' +
        String(current.getDate()).padStart(2, '0');
      const entry = dataByDate[key] || { orders: 0, revenue: 0 };
      result.push({
        date: formatDateWithDay(key),
        orders: entry.orders,
        revenue: entry.revenue
      });
      current.setDate(current.getDate() + 1);
    }

    return result;
  };

  const preparePieChartData = () => {
    return bySource.map(s => ({
      name: s.source,
      value: parseInt(s.order_count) || 0,
      revenue: parseFloat(s.revenue) || 0
    }));
  };

  const prepareHourlyData = () => {
    return byHour.map(h => ({
      hour: formatHour(h.hour),
      orders: parseInt(h.order_count) || 0
    }));
  };

  // Check if viewing a single day (today, yesterday, or custom with same start/end)
  const isSingleDay = period === 'today' || period === 'yesterday' ||
    (period === 'custom' && startDate && endDate && startDate === endDate);

  // Prepare hourly trend data (for single-day view)
  const prepareHourlyTrendData = () => {
    if (byHour.length === 0) return [];
    // Sort by hour and format for line chart
    const sorted = [...byHour].sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
    return sorted.map(h => ({
      date: formatHour(h.hour),
      orders: parseInt(h.order_count) || 0,
      revenue: parseFloat(h.revenue) || 0
    }));
  };

  // Prepare item trend chart data
  const prepareItemTrendData = () => {
    if (itemTrend.length === 0) return [];
    // Group by date, each item is a separate key
    const byDate = {};
    itemTrend.forEach(row => {
      const dateKey = typeof row.order_date === 'string' ? row.order_date.slice(0, 10) : row.order_date;
      if (!byDate[dateKey]) byDate[dateKey] = { date: formatDateWithDay(dateKey) };
      byDate[dateKey][row.item_name] = parseFloat(row.daily_revenue) || 0;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  };

  const TREND_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  // Categorize tips by source and order type
  // Online: GrubHub, ChowNow, DoorDash, Caviar, Toast, ToastOnline (Uber)
  //   - Delivery: order_type = 'Delivery' or 'Self Delivery'
  //   - Pickup: order_type = 'Pickup' or 'Curbside'
  // In Store:
  //   - Counter: source = 'InStore'
  //   - Kiosk: source = 'Kiosk'
  const categorizeTips = () => {
    const categories = {
      onlineDelivery: 0,
      onlinePickup: 0,
      counter: 0,
      kiosk: 0
    };

    tipsByDiningType.forEach(s => {
      const tips = parseFloat(s.total_tips) || 0;
      const source = (s.source || '').toLowerCase();
      const orderType = (s.order_type || '').toLowerCase();

      if (source === 'kiosk') {
        categories.kiosk += tips;
      } else if (source === 'instore') {
        categories.counter += tips;
      } else {
        // Online sources
        if (orderType === 'delivery' || orderType === 'self delivery') {
          categories.onlineDelivery += tips;
        } else {
          // Pickup, Curbside, etc.
          categories.onlinePickup += tips;
        }
      }
    });

    return categories;
  };

  const tipCategories = categorizeTips();
  const categorizedTotal = tipCategories.onlineDelivery + tipCategories.onlinePickup + tipCategories.kiosk + tipCategories.counter;
  const overviewTipsTotal = parseFloat(overview?.total_tips) || 0;
  const tipsMismatch = Math.abs(categorizedTotal - overviewTipsTotal) > 0.01;

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-900 text-red-200 p-4 rounded">
          {error}
          <button onClick={fetchData} className="ml-4 underline">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      {/* Filter Bar - Shared across tabs */}
      <FilterBar
        period={period}
        onPeriodChange={setPeriod}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        sources={sources}
        selectedSources={selectedSources}
        onSourceChange={setSelectedSources}
        orderTypes={orderTypes}
        selectedOrderTypes={selectedOrderTypes}
        onOrderTypeChange={setSelectedOrderTypes}
        availableTags={availableTags}
        selectedTag={selectedTag}
        onTagChange={setSelectedTag}
        itemSearch={itemSearch}
        onItemSearchChange={setItemSearch}
        onItemSearchApply={fetchData}
        onRefresh={activeTab === 'analytics' ? fetchData : () => fetchOrderHistory(1)}
        onExport={handleExport}
        loading={loading || orderLoading}
      />

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-700">
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative ${
            activeTab === 'analytics'
              ? 'text-blue-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Analytics
          {activeTab === 'analytics' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-4 py-2 text-sm font-medium transition-colors relative flex items-center gap-2 ${
            activeTab === 'orders'
              ? 'text-blue-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Orders
          {activeTab === 'orders' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
          )}
        </button>
        {activeTab === 'orders' && (
          <div className="ml-auto flex items-center gap-2 pb-1">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                soundEnabled
                  ? 'bg-green-900/50 text-green-400'
                  : 'bg-gray-700 text-gray-400'
              }`}
              title={soundEnabled ? 'Sound enabled for new customers' : 'Sound disabled'}
            >
              {soundEnabled ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
              {soundEnabled ? 'On' : 'Off'}
            </button>
            <span className="text-xs text-gray-500">Auto-refresh: 30s</span>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'orders' ? (
        /* Orders Tab */
        <div>
          {/* Order-specific filters */}
          <div className="flex flex-wrap gap-2 mb-3">
            <input
              type="text"
              placeholder="Search customer name..."
              value={orderFilters.search}
              onChange={e => setOrderFilters(f => ({ ...f, search: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && fetchOrderHistory(1)}
              className="bg-gray-700 text-white text-sm px-3 py-1.5 rounded border border-gray-600 w-48"
            />
            <select
              value={orderFilters.source}
              onChange={e => { setOrderFilters(f => ({ ...f, source: e.target.value })); }}
              className="bg-gray-700 text-white text-sm px-2 py-1.5 rounded border border-gray-600"
            >
              <option value="">All Sources</option>
              {sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={orderFilters.orderType}
              onChange={e => { setOrderFilters(f => ({ ...f, orderType: e.target.value })); }}
              className="bg-gray-700 text-white text-sm px-2 py-1.5 rounded border border-gray-600"
            >
              <option value="">All Order Types</option>
              {orderTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select
              value={orderFilters.paymentType}
              onChange={e => { setOrderFilters(f => ({ ...f, paymentType: e.target.value })); }}
              className="bg-gray-700 text-white text-sm px-2 py-1.5 rounded border border-gray-600"
            >
              <option value="">All Payment Types</option>
              {['CASH', 'CREDIT', 'CREDIT_CARD', 'OTHER'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={orderFilters.promoCode}
              onChange={e => { setOrderFilters(f => ({ ...f, promoCode: e.target.value })); }}
              className="bg-gray-700 text-white text-sm px-2 py-1.5 rounded border border-gray-600"
            >
              <option value="">All Promo Codes</option>
              {availablePromoCodes.map(code => <option key={code} value={code}>{code}</option>)}
            </select>
            <button
              onClick={() => fetchOrderHistory(1)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded"
            >
              Apply
            </button>
            <button
              onClick={() => { setOrderFilters({ source: '', platform: '', paymentType: '', orderType: '', search: '', promoCode: '' }); }}
              className="bg-gray-600 hover:bg-gray-500 text-white text-sm px-3 py-1.5 rounded"
            >
              Clear
            </button>
          </div>

          {/* Row count */}
          <div className="text-sm text-gray-400 mb-2">
            {orderPagination.total} orders found — Page {orderPagination.page} of {orderPagination.totalPages || 1}
          </div>

          {/* Orders Table */}
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700 bg-gray-750">
                    {[
                      { key: 'order_date', label: 'Date' },
                      { key: 'order_time', label: 'Time' },
                      { key: 'source', label: 'Source' },
                      { key: 'order_type', label: 'Type' },
                      { key: 'customer_name', label: 'Customer' },
                      { key: 'notes', label: '', sortable: false },
                      { key: 'order_total', label: 'Total' },
                      { key: 'tip', label: 'Tip' },
                      { key: 'payment_type', label: 'Payment' },
                      { key: 'tags', label: 'Tags' },
                    ].map(col => (
                      <th
                        key={col.key}
                        className={`px-3 py-2 text-left font-medium text-gray-400 whitespace-nowrap ${col.sortable !== false && col.key !== 'tags' ? 'cursor-pointer hover:text-white' : ''}`}
                        onClick={() => {
                          if (col.key === 'tags' || col.sortable === false) return;
                          const newDir = orderSort.sortBy === col.key && orderSort.sortDir === 'ASC' ? 'DESC' : 'ASC';
                          setOrderSort({ sortBy: col.key, sortDir: newDir });
                        }}
                      >
                        {col.label}
                        {orderSort.sortBy === col.key && (
                          <span className="ml-1">{orderSort.sortDir === 'ASC' ? '▲' : '▼'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orderLoading ? (
                    <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">Loading...</td></tr>
                  ) : orderHistory.length === 0 ? (
                    <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">No orders found</td></tr>
                  ) : orderHistory.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr
                        className={`border-b border-gray-700/50 cursor-pointer transition-colors ${
                          row.is_new_customer ? 'bg-green-900/40 hover:bg-green-900/50' : 'hover:bg-gray-750'
                        } ${expandedOrderId === row.id ? 'bg-gray-700' : ''}`}
                        onClick={() => setExpandedOrderId(expandedOrderId === row.id ? null : row.id)}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">{row.order_date?.slice(0, 10)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.order_time?.slice(0, 5)}</td>
                        <td className="px-3 py-2">{row.source}</td>
                        <td className="px-3 py-2">{row.order_type}</td>
                        <td className="px-3 py-2 flex items-center gap-1">
                          {row.customer_name || '-'}
                          {row.is_new_customer && (
                            <span className="text-xs bg-green-600 text-white px-1.5 py-0.5 rounded">NEW</span>
                          )}
                        </td>
                        <td className="px-1 py-2 text-center">
                          {row.notes && (
                            <div className="group relative inline-block">
                              <svg className="w-4 h-4 text-yellow-400 cursor-help" fill="currentColor" viewBox="0 0 20 20" title="Special Instructions">
                                <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" />
                              </svg>
                              <div className="hidden group-hover:block absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-yellow-900 text-yellow-100 text-xs rounded shadow-lg whitespace-normal">
                                <div className="font-medium mb-1">Special Instructions:</div>
                                {row.notes}
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-yellow-900"></div>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">{row.order_total != null ? formatCurrency(row.order_total) : '-'}</td>
                        <td className="px-3 py-2 text-right">{row.tip != null && row.tip > 0 ? formatCurrency(row.tip) : '-'}</td>
                        <td className="px-3 py-2">{row.payment_type || '-'}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(row.tags || []).slice(0, 3).map((tag, i) => (
                              <span key={i} className="text-xs bg-gray-600 text-gray-200 px-1.5 py-0.5 rounded">
                                {tag}
                              </span>
                            ))}
                            {(row.tags || []).length > 3 && (
                              <span className="text-xs text-gray-500">+{row.tags.length - 3}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded row details */}
                      {expandedOrderId === row.id && (
                        <tr className="bg-gray-750">
                          <td colSpan={10} className="px-4 py-3">
                            <div className="grid grid-cols-2 gap-4">
                              {/* Order Details */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-300 mb-2">Order Details</h4>
                                <div className="text-sm space-y-1 text-gray-400">
                                  <p><span className="text-gray-500">Platform:</span> {row.platform || '-'}</p>
                                  <p><span className="text-gray-500">External ID:</span> {row.external_order_id || '-'}</p>
                                  <p><span className="text-gray-500">Subtotal:</span> {row.subtotal != null ? formatCurrency(row.subtotal) : '-'}</p>
                                  <p><span className="text-gray-500">Tax:</span> {row.tax != null ? formatCurrency(row.tax) : '-'}</p>
                                  <p><span className="text-gray-500">All Tags:</span> {(row.tags || []).join(', ') || '-'}</p>
                                </div>
                                {/* Discount Info - show only if present */}
                                {(row.discount_amount || row.promo_code) && (
                                  <div className="mt-3">
                                    <h4 className="text-sm font-medium text-green-400 mb-1">Discount Applied</h4>
                                    <div className="text-sm text-green-200 bg-green-900/30 px-2 py-1 rounded flex items-center gap-2">
                                      {row.promo_code && <span className="font-mono bg-green-800/50 px-1.5 py-0.5 rounded">{row.promo_code}</span>}
                                      {row.discount_amount && <span>-{formatCurrency(row.discount_amount)}</span>}
                                    </div>
                                  </div>
                                )}
                                {/* Notes/Special Instructions */}
                                {row.notes && (
                                  <div className="mt-3">
                                    <h4 className="text-sm font-medium text-yellow-400 mb-1">Special Instructions</h4>
                                    <p className="text-sm text-yellow-200 bg-yellow-900/30 px-2 py-1 rounded">{row.notes}</p>
                                  </div>
                                )}
                              </div>
                              {/* Items */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-300 mb-2">Items</h4>
                                <div className="text-sm space-y-1 max-h-40 overflow-y-auto">
                                  {row.items && Array.isArray(row.items) ? (
                                    row.items.map((item, i) => (
                                      <div key={i} className="flex justify-between text-gray-400">
                                        <span>{item.quantity}x {item.name}</span>
                                        <span className="text-gray-500">{formatCurrency(item.price || 0)}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-gray-500">No items data</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {orderPagination.totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 py-3 border-t border-gray-700">
                <button
                  onClick={() => fetchOrderHistory(1)}
                  disabled={orderPagination.page <= 1}
                  className="px-2 py-1 text-sm rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40"
                >
                  ««
                </button>
                <button
                  onClick={() => fetchOrderHistory(orderPagination.page - 1)}
                  disabled={orderPagination.page <= 1}
                  className="px-2 py-1 text-sm rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40"
                >
                  «
                </button>
                <span className="text-sm text-gray-400">
                  Page {orderPagination.page} of {orderPagination.totalPages}
                </span>
                <button
                  onClick={() => fetchOrderHistory(orderPagination.page + 1)}
                  disabled={orderPagination.page >= orderPagination.totalPages}
                  className="px-2 py-1 text-sm rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40"
                >
                  »
                </button>
                <button
                  onClick={() => fetchOrderHistory(orderPagination.totalPages)}
                  disabled={orderPagination.page >= orderPagination.totalPages}
                  className="px-2 py-1 text-sm rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40"
                >
                  »»
                </button>
              </div>
            )}
          </div>
        </div>
      ) : loading && !overview ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading analytics...</p>
        </div>
      ) : (
        /* Analytics Tab */
        <DashboardGrid>
          {/* Overview Stats */}
          <DashboardGrid.Row cols={6}>
            <DashboardGrid.StatCard
              label="Total Orders"
              value={overview?.total_orders || 0}
            />
            <DashboardGrid.StatCard
              label="Total Revenue"
              value={formatCurrency(overview?.total_revenue)}
            />
            <DashboardGrid.StatCard
              label="Avg Order Value"
              value={formatCurrency(overview?.avg_order_value)}
            />
            <DashboardGrid.StatCard
              label="Tips"
              value={formatCurrency(overview?.total_tips)}
            >
              <div className="mt-1 space-y-0.5 text-xs">
                <div className="flex justify-between text-gray-400">
                  <span>Online Delivery:</span>
                  <span className="text-gray-300">{formatCurrency(tipCategories.onlineDelivery)}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Online Pickup:</span>
                  <span className="text-gray-300">{formatCurrency(tipCategories.onlinePickup)}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>In Store Kiosk:</span>
                  <span className="text-gray-300">{formatCurrency(tipCategories.kiosk)}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>In Store Counter:</span>
                  <span className="text-gray-300">{formatCurrency(tipCategories.counter)}</span>
                </div>
              </div>
            </DashboardGrid.StatCard>
            <DashboardGrid.StatCard
              label="Cash Revenue"
              value={formatCurrency(overview?.cash_revenue)}
              subValue={`${overview?.cash_orders || 0} orders`}
            />
            <DashboardGrid.StatCard
              label="Expected Cash"
              value={formatCurrency(overview?.expected_cash_drawer)}
              subValue="sales + tips"
            />
          </DashboardGrid.Row>

          {/* Secondary Stats */}
          <DashboardGrid.Row cols={6}>
            <DashboardGrid.StatCard
              label="Unique Customers"
              value={overview?.unique_customers || 0}
            />
            <DashboardGrid.StatCard
              label="Card Revenue"
              value={formatCurrency(overview?.card_revenue)}
            />
            <DashboardGrid.StatCard
              label="Cash Tips"
              value={formatCurrency(overview?.cash_tips)}
            />
            <DashboardGrid.StatCard
              label="With Phone"
              value={overview?.orders_with_phone || 0}
              subValue={`${overview?.total_orders > 0 ? ((overview?.orders_with_phone / overview?.total_orders) * 100).toFixed(0) : 0}%`}
            />
            <DashboardGrid.StatCard
              label="With Email"
              value={overview?.orders_with_email || 0}
              subValue={`${overview?.total_orders > 0 ? ((overview?.orders_with_email / overview?.total_orders) * 100).toFixed(0) : 0}%`}
            />
            <DashboardGrid.StatCard
              label="Avg Tip"
              value={formatCurrency(parseFloat(overview?.total_tips || 0) / Math.max(parseInt(overview?.total_orders || 0), 1))}
              subValue="per order"
            />
          </DashboardGrid.Row>

          {/* Main Charts Row */}
          <DashboardGrid.Row cols={2}>
            {/* Daily/Hourly Trends Line Chart */}
            <Widget title={isSingleDay ? "Hourly Trends" : "Daily Trends"}>
              <LineChart
                data={isSingleDay ? prepareHourlyTrendData() : prepareLineChartData()}
                xKey="date"
                lines={[
                  { dataKey: 'orders', name: 'Orders', color: '#3B82F6' },
                  { dataKey: 'revenue', name: 'Revenue ($)', color: '#10B981' }
                ]}
                height={280}
              />
            </Widget>

            {/* Period Comparison */}
            <ComparisonWidget
              period={period}
              source={getSourceParam() || 'all'}
            />
          </DashboardGrid.Row>

          {/* Source Charts Row */}
          <DashboardGrid.Row cols={2}>
            {/* Orders by Source - Pie Chart */}
            <Widget title="Orders by Source">
              <PieChart
                data={preparePieChartData()}
                dataKey="value"
                nameKey="name"
                height={280}
              />
            </Widget>

            {/* Revenue by Source - Bar Chart */}
            <Widget title="Revenue by Source">
              <BarChart
                data={bySource.map(s => ({
                  source: s.source,
                  revenue: parseFloat(s.revenue) || 0
                }))}
                xKey="source"
                bars={[{ dataKey: 'revenue', name: 'Revenue', color: '#F59E0B' }]}
                height={280}
                colorByIndex
                formatTooltip={(value) => formatCurrency(value)}
              />
            </Widget>
          </DashboardGrid.Row>

          {/* Financial Charts Row */}
          <DashboardGrid.Row cols={2}>
            {/* Tips by Source */}
            <Widget title="Tips by Source">
              <BarChart
                data={tipsBySource.filter(s => parseFloat(s.total_tips) > 0).map(s => ({
                  source: s.source,
                  tips: parseFloat(s.total_tips) || 0,
                  tipPct: parseFloat(s.tip_percentage) || 0
                }))}
                xKey="source"
                bars={[{ dataKey: 'tips', name: 'Tips ($)', color: '#10B981' }]}
                height={280}
                colorByIndex
                formatTooltip={(value) => formatCurrency(value)}
              />
            </Widget>

            {/* Tips by Order Type */}
            <Widget title="Tips by Order Type">
              <BarChart
                data={tipsByType.filter(s => parseFloat(s.total_tips) > 0).map(s => ({
                  type: s.order_type,
                  tips: parseFloat(s.total_tips) || 0,
                  tipPct: parseFloat(s.tip_percentage) || 0
                }))}
                xKey="type"
                bars={[{ dataKey: 'tips', name: 'Tips ($)', color: '#6366F1' }]}
                height={280}
                colorByIndex
                formatTooltip={(value) => formatCurrency(value)}
              />
            </Widget>
          </DashboardGrid.Row>

          {/* Tips by Dining Type Table */}
          {tipsByDiningType.length > 0 && (
            <Widget title="Tips by Dining Type (Source + Order Type)">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Dining Type</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Orders</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Orders w/ Tips</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Total Tips</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Avg Tip</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Tip %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tipsByDiningType.map((row, i) => (
                      <tr key={i} className="border-b border-gray-700 hover:bg-gray-750">
                        <td className="px-4 py-3 font-medium">{row.dining_type}</td>
                        <td className="px-4 py-3 text-right">{row.order_count}</td>
                        <td className="px-4 py-3 text-right">{row.orders_with_tips}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(row.total_tips)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(row.avg_tip)}</td>
                        <td className="px-4 py-3 text-right">{row.tip_percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Widget>
          )}

          {/* Payment Type Breakdown */}
          <DashboardGrid.Row cols={2}>
            <Widget title="Payment Type Breakdown">
              <PieChart
                data={payments.filter(p => p.payment_type !== 'Unknown').map(p => ({
                  name: p.payment_type,
                  value: parseFloat(p.total_amount) || 0
                }))}
                dataKey="value"
                nameKey="name"
                height={280}
              />
            </Widget>
          </DashboardGrid.Row>

          {/* Cash Reconciliation Table */}
          {cashRecon.length > 0 && (
            <Widget title="Daily Cash Reconciliation">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Date</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Total Sales</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Total Tips</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Cash Sales</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Cash Tips</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400 bg-green-900/30">Expected Cash</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Card Sales</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashRecon.map((row, i) => (
                      <tr key={i} className="border-b border-gray-700 hover:bg-gray-750">
                        <td className="px-4 py-3 font-medium">{formatDate(row.order_date)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(row.total_sales)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(row.total_tips)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(row.cash_sales)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(row.cash_tips)}</td>
                        <td className="px-4 py-3 text-right font-bold text-green-400 bg-green-900/20">{formatCurrency(row.expected_cash)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(row.card_sales)}</td>
                        <td className="px-4 py-3 text-right">{row.total_orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Widget>
          )}

          {/* Bottom Charts Row */}
          <DashboardGrid.Row cols={3}>
            {/* Hourly Distribution */}
            <Widget title="Orders by Hour">
              <BarChart
                data={prepareHourlyData()}
                xKey="hour"
                bars={[{ dataKey: 'orders', name: 'Orders', color: '#8B5CF6' }]}
                height={250}
                layout="horizontal"
              />
            </Widget>

            {/* Top Tags - Item Counts & Revenue */}
            <Widget title="Top Tags (Items Sold)">
              <BarChart
                data={tags.map(t => ({
                  tag: t.tag_name,
                  items: parseInt(t.item_count) || parseInt(t.count) || 0,
                  revenue: parseFloat(t.item_revenue) || 0
                }))}
                xKey="tag"
                bars={[
                  { dataKey: 'items', name: 'Items Sold', color: '#EC4899' },
                  { dataKey: 'revenue', name: 'Revenue ($)', color: '#F59E0B' }
                ]}
                height={250}
                showLegend
                formatTooltip={(value, name) => name.includes('Revenue') ? formatCurrency(value) : value}
              />
            </Widget>

            {/* Top Items */}
            <Widget title="Top Items">
              <BarChart
                data={topItems.map(i => ({
                  item: i.item_name,
                  quantity: parseInt(i.total_quantity) || 0
                }))}
                xKey="item"
                bars={[{ dataKey: 'quantity', name: 'Quantity', color: '#06B6D4' }]}
                height={250}
                colorByIndex
              />
            </Widget>
          </DashboardGrid.Row>

          {/* Top Customers by Tag - only visible when a tag is selected */}
          {selectedTag && topCustomersByTag.length > 0 && (
            <Widget title={`Top ${selectedTag} Customers`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Rank</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Customer Name</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Items Ordered</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Spent on Tag</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Total Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCustomersByTag.map((row, i) => (
                      <tr key={row.id || i} className="border-b border-gray-700 hover:bg-gray-750">
                        <td className="px-4 py-3 font-medium text-gray-400">{i + 1}</td>
                        <td className="px-4 py-3 font-medium">{row.name || 'Unknown'}</td>
                        <td className="px-4 py-3 text-right">{row.total_items}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(row.total_spent_on_tag)}</td>
                        <td className="px-4 py-3 text-right">{row.order_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Widget>
          )}

          {/* Item Analytics Section */}
          {itemRevenue.length > 0 && (
            <>
              <Widget title="Item Revenue Rankings">
                <div className="overflow-x-auto">
                  <p className="text-xs text-gray-400 mb-2">Click an item to view its daily trend (max 5)</p>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Item Name</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Qty Sold</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Total Revenue</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Avg Price</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Order Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemRevenue.map((row, i) => {
                        const isSelected = selectedTrendItems.includes(row.item_name);
                        return (
                          <tr
                            key={i}
                            className={`border-b border-gray-700 cursor-pointer transition-colors ${isSelected ? 'bg-blue-900/30' : 'hover:bg-gray-750'}`}
                            onClick={() => handleTrendItemToggle(row.item_name)}
                          >
                            <td className="px-4 py-3 font-medium">
                              {isSelected && <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: TREND_COLORS[selectedTrendItems.indexOf(row.item_name) % TREND_COLORS.length] }} />}
                              {row.item_name}
                            </td>
                            <td className="px-4 py-3 text-right">{parseInt(row.total_quantity) || 0}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(row.total_revenue)}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(row.avg_price)}</td>
                            <td className="px-4 py-3 text-right">{parseInt(row.order_count) || 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Widget>

              {/* Item Trend Chart */}
              {selectedTrendItems.length > 0 && itemTrend.length > 0 && (
                <Widget title={`Daily Revenue: ${selectedTrendItems.join(', ')}`}>
                  <LineChart
                    data={prepareItemTrendData()}
                    xKey="date"
                    lines={selectedTrendItems.map((name, i) => ({
                      dataKey: name,
                      name: name,
                      color: TREND_COLORS[i % TREND_COLORS.length]
                    }))}
                    height={300}
                  />
                </Widget>
              )}
            </>
          )}

          {/* Source Details Table */}
          <Widget title="Source Breakdown">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Source</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Orders</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Revenue</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Tips</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Avg Order</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">With Phone</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Unique Customers</th>
                  </tr>
                </thead>
                <tbody>
                  {bySource.map((row, i) => (
                    <tr key={i} className="border-b border-gray-700 hover:bg-gray-750">
                      <td className="px-4 py-3 font-medium">{row.source}</td>
                      <td className="px-4 py-3 text-right">{row.order_count}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(row.revenue)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(row.total_tips)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(row.avg_order)}</td>
                      <td className="px-4 py-3 text-right">{row.with_phone}</td>
                      <td className="px-4 py-3 text-right">{row.unique_customers || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Widget>
        </DashboardGrid>
      )}
    </div>
  );
}

export default AnalyticsDashboard;
